import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Color, DEFAULT_DATE_FORMAT, fill, PUBLIC_QUOTE_LINKS_PATH, QUOTE_RESPONSE_ROUTE, Size, formatDateOnly, quoteLinkNameProblem, quoteLinkReasonProblem, quoteLinkStateSentence, quoteOptionTotals, t,
  type PublicQuoteAnswer,
} from '@bms2/shared';
import { Button, Checkbox, TextInput, Textarea } from '../components/controls';
import { DataTable, type DataTableColumn } from '../components/composites';
import { Money } from '../components/Money';
import { Text } from '../components/Text';
import { PublicBand } from '../shell/PublicBand';
import { apiFetch } from './records/api';
import styles from './quoteResponse.module.css';

export { PUBLIC_QUOTE_LINKS_PATH, QUOTE_RESPONSE_ROUTE };

type State =
  | { kind: 'reading' }
  | { kind: 'closed'; message: string }
  | { kind: 'ready'; answer: PublicQuoteAnswer; done: 'accepted' | 'declined' | null };

/** The quote's lines: the description takes what the three figures leave. */
const LINE_COLUMNS: readonly DataTableColumn[] = [
  { key: 'description', header: t('quoteLink.column.description') },
  { key: 'quantity', header: t('quoteLink.column.quantity'), align: 'end', numeric: true },
  { key: 'unitPrice', header: t('quoteLink.column.unitPrice'), align: 'end', numeric: true },
  { key: 'amount', header: t('quoteLink.column.amount'), align: 'end', numeric: true },
];

const day = (value: string): string => (value ? formatDateOnly(`${value}T00:00:00.000Z`, DEFAULT_DATE_FORMAT) : '');

/** What a reply's body is, read without trusting it: the page's answer, a refusal's sentence, or nothing. */
async function bodyOf(response: Response): Promise<(Partial<PublicQuoteAnswer> & { message?: string }) | null> {
  return (await response.json().catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null)) as (Partial<PublicQuoteAnswer> & { message?: string }) | null;
}

const isAnswer = (body: Partial<PublicQuoteAnswer> | null): body is PublicQuoteAnswer => Boolean(body && typeof body.state === 'string' && typeof body.number === 'string' && Array.isArray(body.lines));

/** The sentence a reply with no answer reads as: past the hour's limit, an unknown link, or a failure. */
const closedText = (status: number): string => (status === 429 ? t('quoteLink.limited') : status === 404 ? t('quoteLink.notFound') : t('quoteLink.failed'));

/**
 * Where the link a Quote's Send mails lands (piece 22; decision 2650; T1247).
 * 🔨 The page stands outside the shell and holds no session: the customer who
 * opens it has no login and needs none. 🔒 It shows the Quote as it was sent,
 * the optional lines with a tick each, the PDF, Accept with the typed full name
 * and Decline with an optional reason. A link whose Quote is past its date,
 * Void, already answered or replaced by a newer send says where it stands and
 * offers neither. The server decides every refusal; the checks here only save
 * a round trip.
 */
export function QuoteResponsePage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { token = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'reading' });
  const [taken, setTaken] = useState<ReadonlySet<string>>(new Set());
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const path = `${PUBLIC_QUOTE_LINKS_PATH}/${encodeURIComponent(token)}`;

  useEffect(() => {
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${path}`, { signal: controller.signal }, { skipAuthRedirect: true })
      .then(async (response) => {
        const body = await bodyOf(response);
        if (!response.ok || !isAnswer(body)) return setState({ kind: 'closed', message: closedText(response.status) });
        return setState({ kind: 'ready', answer: body, done: null });
      })
      .catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => {
        if (!controller.signal.aborted) setState({ kind: 'closed', message: t('quoteLink.failed') });
      });
    return () => controller.abort();
  }, [apiBaseUrl, path]);

  const answerWith = async (action: 'accept' | 'decline', body: unknown) => {
    setError(null);
    setBusy(action);
    try {
      const response = await apiFetch(`${apiBaseUrl}${path}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, { skipAuthRedirect: true });
      const reply = await bodyOf(response);
      // 🔒 A link answered elsewhere, or whose Quote moved, is told where it now stands (409) and offers nothing more.
      if ((response.ok || response.status === 409) && isAnswer(reply)) return setState({ kind: 'ready', answer: reply, done: response.ok ? (action === 'accept' ? 'accepted' : 'declined') : null });
      if (response.status === 400) return setError(reply?.message ?? t('quoteLink.failed'));
      return setState({ kind: 'closed', message: closedText(response.status) });
    } catch {
      // silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person).
      setError(t('quoteLink.failed'));
    } finally {
      setBusy(null);
    }
  };

  const accept = () => {
    const problem = quoteLinkNameProblem(name);
    setNameError(problem);
    if (problem) return;
    void answerWith('accept', { name: name.trim(), optionalLineIds: [...taken] });
  };
  const decline = () => {
    const problem = quoteLinkReasonProblem(reason);
    setReasonError(problem);
    if (problem) return;
    void answerWith('decline', { reason: reason.trim() || null });
  };

  return (
    <main className={styles.page} data-page="quote-response">
      {/* 🔒 The band every page outside the shell opens on, so the customer sees whose quote this is (decision 0690). */}
      <PublicBand />
      <div className={styles.body}>
        <div className={styles.panel} data-quote-response={state.kind === 'ready' ? state.answer.state : state.kind}>
          <Text variant="title">{state.kind === 'ready' ? `${t('quoteLink.title')} ${state.answer.number}` : t('quoteLink.title')}</Text>
          {state.kind === 'reading' ? <Text variant="small">{t('quoteLink.reading')}</Text> : null}
          {state.kind === 'closed' ? <Text variant="body">{state.message}</Text> : null}
          {state.kind === 'ready' ? (
            <QuoteResponseBody
              answer={state.answer}
              done={state.done}
              pdfHref={`${apiBaseUrl}${path}/pdf`}
              taken={taken}
              onTake={(id, checked) => setTaken((held) => {
                const next = new Set(held);
                if (checked) next.add(id);
                else next.delete(id);
                return next;
              })}
              name={name}
              onName={(value) => { setName(value); setNameError(null); }}
              nameError={nameError}
              reason={reason}
              onReason={(value) => { setReason(value); setReasonError(null); }}
              reasonError={reasonError}
              error={error}
              busy={busy}
              onAccept={accept}
              onDecline={decline}
            />
          ) : null}
        </div>
      </div>
    </main>
  );
}

function QuoteResponseBody({ answer, done, pdfHref, taken, onTake, name, onName, nameError, reason, onReason, reasonError, error, busy, onAccept, onDecline }: {
  answer: PublicQuoteAnswer;
  done: 'accepted' | 'declined' | null;
  pdfHref: string;
  taken: ReadonlySet<string>;
  onTake: (id: string, checked: boolean) => void;
  name: string;
  onName: (value: string) => void;
  nameError: string | null;
  reason: string;
  onReason: (value: string) => void;
  reasonError: string | null;
  error: string | null;
  busy: 'accept' | 'decline' | null;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const open = answer.state === 'open' && done === null;
  const amountOf = (cents: number) => <Money cents={cents} currencyCode={answer.currencyCode} />;
  // 🔒 The Total counts the required lines and the optional lines ticked; the optional lines stand apart beneath it (decision 2650).
  const totals = quoteOptionTotals(answer.lines.map((row) => ({ id: row.id, isOptional: row.optional, amountCents: row.amountCents, taxCents: row.taxCents })), [...taken]);
  const stateText = done === 'accepted' ? t('quoteLink.acceptedNow') : done === 'declined' ? t('quoteLink.declinedNow') : answer.state === 'open' ? null : quoteLinkStateSentence(answer.state);
  return (
    <>
      {stateText ? <p className={styles.state} role="status" data-quote-response-state={done ?? answer.state}>{stateText}</p> : null}
      {answer.acceptedBy && done === null ? (
        <Text variant="body">{fill(t('quoteLink.acceptedBy'), { name: answer.acceptedBy?.name ?? '', date: day(answer.acceptedBy.on) })}</Text>
      ) : null}
      {answer.state === 'superseded' ? null : (
        <>
          <dl className={styles.facts}>
            <dt>{t('quoteLink.from')}</dt>
            <dd data-quote-response-from="true">{answer.organizationName}</dd>
            <dt>{t('quoteLink.for')}</dt>
            <dd data-quote-response-for="true">{answer.companyName}</dd>
            <dt>{t('quoteLink.quoteDate')}</dt>
            <dd>{day(answer.quoteDate)}</dd>
            <dt>{t('quoteLink.goodTill')}</dt>
            <dd>{day(answer.expiryDate)}</dd>
          </dl>
          <div className={styles.lines}>
            <DataTable columns={LINE_COLUMNS} data-quote-response-lines={answer.lines.length}>
              {answer.lines.map((row) => (
                <tr key={row.id} data-quote-response-line={row.id} data-optional={row.optional ? 'true' : 'false'}>
                  <td>
                    <span className={styles.description}>{row.description}</span>
                    {row.optional ? (
                      <span className={styles.optional} data-quote-response-optional-line="true">
                        {t('quoteLink.optional')}
                        {open ? <Checkbox size={Size.Small} label={t('quoteLink.include')} checked={taken.has(row.id)} onChange={(checked) => onTake(row.id, checked)} name={`take-${row.id}`} /> : null}
                      </span>
                    ) : null}
                  </td>
                  <td className={styles.number}>{`${row.quantity} ${row.unitName}`.trim()}</td>
                  <td className={styles.number}>{amountOf(row.unitPriceCents)}</td>
                  <td className={styles.number}>{amountOf(row.amountCents)}</td>
                </tr>
              ))}
            </DataTable>
          </div>
          <dl className={styles.totals}>
            <dt>{t('quoteLink.subtotal')}</dt>
            <dd data-quote-response-subtotal={totals.takenCents}>{amountOf(totals.takenCents)}</dd>
            {totals.allTaxCents > 0 ? (
              <>
                <dt>{t('quoteLink.tax')}</dt>
                <dd data-quote-response-tax={totals.takenTaxCents}>{amountOf(totals.takenTaxCents)}</dd>
              </>
            ) : null}
            <dt className={styles.total}>{t('quoteLink.total')}</dt>
            <dd className={styles.total} data-quote-response-total={totals.takenCents + totals.takenTaxCents}>{amountOf(totals.takenCents + totals.takenTaxCents)}</dd>
            {totals.hasOptional ? (
              <>
                <dt>{t('quote.lines.optionalTotal')}</dt>
                <dd data-quote-response-optional={totals.optionalCents + totals.optionalTaxCents}>{amountOf(totals.optionalCents + totals.optionalTaxCents)}</dd>
                <dt>{t('quote.lines.allOptions')}</dt>
                <dd data-quote-response-all-options={totals.allCents + totals.allTaxCents}>{amountOf(totals.allCents + totals.allTaxCents)}</dd>
              </>
            ) : null}
          </dl>
          <a className={styles.pdf} href={pdfHref} target="_blank" rel="noreferrer noopener" data-quote-response-pdf="true">{t('quoteLink.pdf')}</a>
        </>
      )}
      {open ? (
        <>
          <section className={styles.answer} data-quote-response-accept="true">
            <Text variant="subtitle">{t('quoteLink.accept.title')}</Text>
            {totals.hasOptional ? <Text variant="caption">{t('quoteLink.optionalHint')}</Text> : null}
            <TextInput size={Size.Large} label={t('quoteLink.accept.name')} value={name} onChange={onName} required error={nameError} name="quoteResponseName" />
            <Text variant="caption">{t('quoteLink.accept.hint')}</Text>
            <div className={styles.actions}>
              <Button size={Size.Large} color={Color.Navy} text={[t(busy === 'accept' ? 'quoteLink.accept.sending' : 'quoteLink.accept.action')]} actions={[onAccept]} disabled={busy !== null} />
            </div>
          </section>
          <section className={styles.answer} data-quote-response-decline="true">
            <Text variant="subtitle">{t('quoteLink.decline.title')}</Text>
            <Textarea size={Size.Large} label={t('quoteLink.decline.reason')} value={reason} onChange={onReason} error={reasonError} name="quoteResponseReason" rows={2} />
            <div className={styles.actions}>
              <Button size={Size.Medium} color={Color.Inactive} text={[t(busy === 'decline' ? 'quoteLink.decline.sending' : 'quoteLink.decline.action')]} actions={[onDecline]} disabled={busy !== null} />
            </div>
          </section>
        </>
      ) : null}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
    </>
  );
}
